import { useState, useMemo, useEffect, useRef } from 'react';
import { Search, X, Check } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useMyItems } from '@/hooks/useUserItems';
import type { UserItem } from '@/types/userItem';
import { cn } from '@/lib/utils';
import { formatUsdCents } from '@/lib/formatMoney';

interface PickInventoryItemSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Single-select callback. Used when `multi` is false (default). */
  onSelect?: (item: UserItem) => void;
  /** Multi-select callback. Required when `multi` is true. */
  onConfirm?: (items: UserItem[]) => void;
  /** Enable multi-select mode with sticky confirm footer. */
  multi?: boolean;
  /** Accessible title (visually hidden). */
  title?: string;
  emptyHint?: string;
}

/**
 * Bottom-sheet drawer that lets a seller search their inventory and pick one
 * (or many, when `multi`) items to feed into a downstream action.
 *
 * Excludes items already sold or with quantity 0. Caps results at 50.
 */
export function PickInventoryItemSheet({
  open,
  onOpenChange,
  onSelect,
  onConfirm,
  multi = false,
  title = 'Pick an item',
  emptyHint = 'Add an item to your inventory first.',
}: PickInventoryItemSheetProps) {
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: items = [], isLoading } = useMyItems();

  // Reset state each time the sheet opens; autofocus on desktop.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIds(new Set());
      const t = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  const eligible = useMemo(
    () => items.filter((it) => !it.sold_at && (it.quantity ?? 1) > 0),
    [items],
  );

  const filtered = useMemo(() => {
    const raw = query.trim().toLowerCase();
    if (!raw) return eligible.slice(0, 50);

    // Split into tokens; every token must match somewhere (AND search).
    const tokens = raw.split(/\s+/).filter(Boolean);

    const scored = eligible
      .map((it) => {
        const haystackParts = [
          it.title,
          it.set_name,
          it.set_code,
          it.card_number,
          it.description,
          it.condition,
          it.game,
          it.category,
          it.finish_label,
          it.rarity,
        ]
          .filter(Boolean)
          .map((s) => String(s).toLowerCase());
        const haystack = haystackParts.join(' | ');

        // Strip common separators from card numbers so "025" matches "025/204".
        const normalized = haystack.replace(/[\/\-#]/g, ' ');

        const allMatch = tokens.every(
          (t) => haystack.includes(t) || normalized.includes(t),
        );
        if (!allMatch) return null;

        // Simple relevance score: title hits and prefix matches rank higher.
        const title = (it.title || '').toLowerCase();
        let score = 0;
        for (const t of tokens) {
          if (title.startsWith(t)) score += 10;
          else if (title.includes(t)) score += 5;
          if ((it.set_code || '').toLowerCase() === t) score += 8;
          if ((it.card_number || '').toLowerCase() === t) score += 8;
        }
        return { it, score };
      })
      .filter((x): x is { it: UserItem; score: number } => x !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, 50)
      .map((x) => x.it);

    return scored;
  }, [eligible, query]);

  const handleRowTap = (item: UserItem) => {
    if (multi) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(item.id)) next.delete(item.id);
        else next.add(item.id);
        return next;
      });
    } else {
      onSelect?.(item);
    }
  };

  const handleConfirm = () => {
    if (!multi) return;
    const picked = eligible.filter((it) => selectedIds.has(it.id));
    onConfirm?.(picked);
  };

  const selectedCount = selectedIds.size;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="bg-chrome border-white/[0.06] rounded-t-2xl p-0 pb-[env(safe-area-inset-bottom)] h-[85dvh] flex flex-col [&>button.absolute]:hidden"
        bodyClassName="flex flex-col !overflow-hidden !mx-0 !px-0"
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>

        {/* Search */}
        <div className="sticky top-0 px-3 pt-2 pb-3 shrink-0 bg-chrome border-b border-white/[0.06] z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <SearchInput
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, set, or code…"
              className="h-11 pl-9 pr-9 bg-black/20 border-white/10 text-base"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3">
          {isLoading ? (
            <div className="py-12 flex justify-center">
              <LoadingSpinner variant="inline" size="lg" />
            </div>
          ) : eligible.length === 0 ? (
            <div className="py-12 text-center px-6">
              <p className="text-base font-medium">No items in your inventory</p>
              <p className="text-sm text-muted-foreground mt-1">{emptyHint}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center px-6">
              <p className="text-sm text-muted-foreground">No items match "{query}"</p>
            </div>
          ) : (
            <ul className="flex flex-col">
              {filtered.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  selected={selectedIds.has(item.id)}
                  multi={multi}
                  onTap={() => handleRowTap(item)}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Sticky confirm footer (multi-select only) */}
        {multi && (
          <div className="shrink-0 px-3 pt-4 pb-3 bg-chrome">
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={selectedCount === 0}
              className="w-full h-11"
            >
              {selectedCount === 0
                ? 'Select items'
                : `Add ${selectedCount} item${selectedCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ItemRow({
  item,
  selected,
  multi,
  onTap,
}: {
  item: UserItem;
  selected: boolean;
  multi: boolean;
  onTap: () => void;
}) {
  const thumb = item.images?.[0]?.image_url || item.stock_image_url || null;
  const conditionLabel = item.condition
    ? item.condition
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const subtitle = [item.set_name, conditionLabel].filter(Boolean).join(' · ');
  const price =
    typeof item.asking_price_cents === 'number'
      ? `${formatUsdCents(item.asking_price_cents)}`
      : null;

  return (
    <li>
      <button
        type="button"
        onClick={onTap}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left',
          selected ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04] active:bg-white/[0.07]',
        )}
      >
        <div className="w-10 h-10 rounded-md bg-white/[0.04] border border-white/[0.06] overflow-hidden shrink-0 flex items-center justify-center">
          {thumb ? (
            <img src={thumb} alt="" loading="lazy" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[9px] text-muted-foreground">No photo</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium leading-tight truncate">{item.title}</div>
          {subtitle && (
            <div className="text-[11px] text-muted-foreground leading-tight mt-0.5 truncate">
              {subtitle}
            </div>
          )}
        </div>
        {price && (
          <span className="text-sm font-medium text-active-gold tabular-nums shrink-0">
            {price}
          </span>
        )}
        {multi && (
          <div
            className={cn(
              'shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors',
              selected
                ? 'bg-active-gold border-active-gold text-black'
                : 'border-white/25 bg-transparent',
            )}
            aria-hidden
          >
            {selected && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
          </div>
        )}
      </button>
    </li>
  );
}
